/**
 * ISO-3166 alpha-2 helpers for buyer / user / distributor country.
 *
 * Storage is the 2-letter code; localized names are display-only. Legacy
 * values (Suisse, France, …) are mapped back to ISO when possible.
 */
import { getDataSet, reduce } from 'iso3166-2-db';

export const EXTRA_COUNTRIES = {
    XK: { name: 'Kosovo' },
};

const DISPLAY_LANGS = ['en', 'fr', 'de', 'it'];

const EXTRA_ALIASES = {
    unknown: '',
    inconnu: '',
    cotedivoire: 'CI',
    ivorycoast: 'CI',
    kosovo: 'XK',
    zaire: 'CD',
    yugoslavia: 'YU',
    yougoslavie: 'YU',
    serbiaandmontenegro: 'RS',
    netherlandsantilles: 'AN',
    unitedkingdom: 'GB',
    greatbritain: 'GB',
    uk: 'GB',
    unitedstates: 'US',
    unitedstatesofamerica: 'US',
};

let alias_map_cache = null;
const countries_by_iso_cache = Object.create(null);

export function normalizeCountryLabel(value) {
    return String(value == null ? '' : value)
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '');
}

/**
 * Localized ISO-3166 country map. Cached per language: iso3166-2-db's
 * getDataSet()/reduce() is expensive, and phone option sorting used to call
 * this thousands of times per UserForm mount (multi-second UI freeze).
 */
export function countriesByIso(lang) {
    const code = String(lang || 'fr').substring(0, 2);
    if (countries_by_iso_cache[code])
        return countries_by_iso_cache[code];
    const map = {
        ...reduce(getDataSet(), code),
        ...EXTRA_COUNTRIES,
    };
    countries_by_iso_cache[code] = map;
    return map;
}

function aliasMap() {
    if (alias_map_cache)
        return alias_map_cache;

    const map = { ...EXTRA_ALIASES };
    DISPLAY_LANGS.forEach((lang) => {
        const countries = countriesByIso(lang);
        Object.keys(countries).forEach((iso) => {
            map[iso.toLowerCase()] = iso;
            const name = countries[iso]?.name;
            if (name)
                map[normalizeCountryLabel(name)] = iso;
        });
    });
    alias_map_cache = map;
    return map;
}

export function countryIsoFromValue(value) {
    if (value == null)
        return '';
    const raw = String(value).trim();
    if (raw === '')
        return '';
    if (/^[A-Za-z]{2}$/.test(raw))
        return raw.toUpperCase();
    const mapped = aliasMap()[normalizeCountryLabel(raw)];
    return mapped ? String(mapped).toUpperCase() : '';
}

export function countryDisplayName(value, lang) {
    if (value == null || String(value).trim() === '')
        return '';
    const iso = countryIsoFromValue(value);
    if (!iso)
        return String(value);
    const name = countriesByIso(lang)[iso]?.name;
    return name || iso;
}
